import { useEffect } from 'react';
import styled from 'styled-components';

import { Text } from '@yoweb/ui/components/typography';
import { CloseIcon } from '@yoweb/ui/components/Icon/CloseIcon';
import { getColor, getRadii, getSpace } from '@yoweb/ui/styles/utils/theme';
import { useError } from '@yoweb/ui/components/form';
import type { ErrorObject } from '@yoweb/ui/components/form/ErrorDisplay';
import { isObjectLike } from '@yoweb/utils/isObjectLike';
import { media } from '@yoweb/ui/styles/utils';
import ErrorIcon from '@yoweb/ui/components/Icon/ErrorIcon';
import { useMobileOnly } from '@yoweb/ui/components/Responsive';

export type ErrorBannerProps = {
  error?: ErrorObject | boolean;
  dismissible?: boolean;
  onErrorDismissed?(): void;
  withBorderRadius?: boolean;
};

/**
 * Displays a dismissible error in a critical Light colored container.
 */
export const ErrorBanner = ({
  error,
  onErrorDismissed,
  dismissible = true,
  withBorderRadius = true,
}: ErrorBannerProps) => {
  const errorDisplay = useError();
  const isMobile = useMobileOnly();

  useEffect(() => {
    if (!error) {
      return;
    }

    errorDisplay.set(isObjectLike(error) ? (error as ErrorObject) : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const dismissError = () => {
    errorDisplay.clear();
    onErrorDismissed?.();
  };

  if (!error) {
    return null;
  }

  const { title, message } = errorDisplay;

  return (
    <ErrorContainer withBorderRadius={withBorderRadius} data-testid="error-banner">
      <IconWrapper>
        <ErrorIcon size={24} variant="danger" />
      </IconWrapper>
      <div>
        {title && (
          <Text as="span" weight="bold" size="md">
            {title}
          </Text>
        )}
        {title && message && <>{isMobile ? <br /> : <Text as="span">&nbsp;</Text>}</>}
        {message && (
          <Text as="span" size="md">
            {message}
          </Text>
        )}
      </div>
      {dismissible && (
        <DismissButton onClick={dismissError} type="button">
          <CloseIcon size={16} />
        </DismissButton>
      )}
    </ErrorContainer>
  );
};

const ErrorContainer = styled.div<{ withBorderRadius: boolean }>`
  background-color: ${getColor('criticalLight')};
  border-top: 1px solid ${getColor('base100')};
  border-radius: ${({ withBorderRadius }) => (withBorderRadius ? getRadii('medium') : 0)};
  column-gap: ${getSpace('small4')};
  display: flex;
  padding: ${getSpace('normal1')};
  width: 100%;

  ${media.md`
    align-items: center;
    padding: ${getSpace('normal2')} ${getSpace('normal1')};
  `}
`;

const DismissButton = styled.button`
  background: transparent;
  border: 0;
  cursor: pointer;
  display: flex;
  padding: 0;
  position: relative;

  svg {
    align-self: flex-start;
  }

  ${media.md`
    svg {
      align-self: center;
    }
  `}
`;

const IconWrapper = styled.div`
  align-self: baseline;
  color: ${getColor('critical')};
  display: flex;
`;
